import { supabase } from "@/integrations/supabase/client";

export type FieldType =
  | "text"
  | "textarea"
  | "email"
  | "phone"
  | "date"
  | "number"
  | "switch"
  | "checkbox_group"
  | "yes_no"
  | "chips"
  | "select"
  | "consent"
  | "signature"
  | "password";

export interface FormField {
  key: string;
  label: string;
  type: FieldType;
  enabled: boolean;
  required?: boolean;
  hint?: string;
  placeholder?: string;
  options?: string[];
  /** system fields map to real database columns and cannot be deleted */
  system?: boolean;
  /** only shown when this other field (switch) is true */
  showIf?: string;
}

export interface FormSection {
  id: string;
  title: string;
  enabled: boolean;
  fields: FormField[];
}

export interface ClientRecordFormConfig {
  intro: string;
  consent_text: string;
  sections: FormSection[];
}

export const HEALTH_CONDITIONS = [
  "Gestante",
  "Hipertensão",
  "Problema cardíaco",
  "Trombose",
  "Cirurgia recente",
  "Alergias",
  "Outro",
];

export const TREATMENT_TYPES = [
  "Massagem relaxante",
  "Drenagem linfática",
  "Vacuoterapia glúteos",
  "Outro",
];

export const PRESSURE_LEVELS = ["Leve", "Média", "Firme"];

export const AROMA_OPTIONS = [
  "Lavanda (relaxante)",
  "Eucalipto (respiratório)",
  "Hortelã-pimenta (energizante)",
  "Citrinos (revigorante)",
  "Camomila (calmante)",
  "Sem preferência",
];

export const CONSENT_TEXT = `Declaro que informei corretamente meu estado de saúde e estou ciente de que os tratamentos estéticos e de bem-estar realizados neste espaço possuem finalidade estética e de relaxamento, não substituindo avaliação ou tratamento médico. Comprometo-me a informar qualquer alteração de saúde antes de cada sessão.`;

export const DEFAULT_INTRO =
  "As informações prestadas são confidenciais e destinam-se a adaptar a sessão às suas necessidades com segurança.";

export const defaultFormConfig = (): ClientRecordFormConfig => ({
  intro: DEFAULT_INTRO,
  consent_text: CONSENT_TEXT,
  sections: [
    {
      id: "personal",
      title: "Dados pessoais",
      enabled: true,
      fields: [
        { key: "full_name", label: "Nome completo", type: "text", enabled: true, required: true, system: true },
        { key: "phone", label: "Contato", type: "phone", enabled: true, required: true, system: true },
        { key: "email", label: "Email", type: "email", enabled: true, required: true, system: true },
        { key: "staff_name", label: "Colaborador(a)", type: "select", enabled: true, system: true, hint: "Selecione o(a) profissional que irá realizar o seu atendimento.", placeholder: "Selecione...", options: ["Thaiane", "Outro"] },
        { key: "session_date", label: "Data da sessão", type: "date", enabled: true, system: false },
        { key: "profession", label: "Profissão", type: "text", enabled: true, system: true },
        { key: "emergency_name", label: "Contacto de emergência (nome)", type: "text", enabled: true, system: true },
        { key: "emergency_phone", label: "Contacto de emergência (telefone)", type: "phone", enabled: true, system: true },
      ],
    },
    {
      id: "health",
      title: "Saúde",
      enabled: true,
      fields: [
        { key: "is_pregnant", label: "Gestante", type: "yes_no", enabled: true, system: true, options: ["Sim", "Não"] },
        { key: "pregnancy_weeks", label: "Se sim, quantas semanas?", type: "text", enabled: true, system: true, showIf: "is_pregnant" },
        { key: "has_hypertension", label: "Hipertensão", type: "yes_no", enabled: true, system: false, options: ["Sim", "Não"] },
        { key: "has_heart_problem", label: "Problema cardíaco", type: "yes_no", enabled: true, system: false, options: ["Sim", "Não"] },
        { key: "has_thrombosis", label: "Trombose", type: "yes_no", enabled: true, system: false, options: ["Sim", "Não"] },
        { key: "has_surgery", label: "Cirurgia recente", type: "yes_no", enabled: true, system: false, options: ["Sim", "Não"] },
        { key: "has_allergies", label: "Alergias", type: "yes_no", enabled: true, system: false, options: ["Sim", "Não"] },
        { key: "allergies", label: "Se sim, especifique:", type: "textarea", enabled: true, system: true, showIf: "has_allergies" },
        { key: "has_other_health", label: "Outro", type: "yes_no", enabled: true, system: false, options: ["Sim", "Não"] },
        { key: "health_other_detail", label: "Se sim, especifique:", type: "text", enabled: true, system: false, showIf: "has_other_health" },
      ],
    },
    {
      id: "preferences",
      title: "Preferências de tratamento",
      enabled: true,
      fields: [
        { key: "treat_relax", label: "Massagem relaxante", type: "yes_no", enabled: true, system: false, options: ["Sim", "Não"] },
        { key: "treat_drain", label: "Drenagem linfática", type: "yes_no", enabled: true, system: false, options: ["Sim", "Não"] },
        { key: "treat_vaccum", label: "Vacuoterapia glúteos", type: "yes_no", enabled: true, system: false, options: ["Sim", "Não"] },
        { key: "has_other_treat", label: "Outro", type: "yes_no", enabled: true, system: false, options: ["Sim", "Não"] },
        { key: "treat_other_detail", label: "Se sim, especifique:", type: "text", enabled: true, system: false, showIf: "has_other_treat" },
        {
          key: "pressure_preference",
          label: "Pressão desejada:",
          type: "yes_no",
          enabled: true,
          system: true,
          options: PRESSURE_LEVELS,
        },
        {
          key: "avoid_areas",
          label: "Alguma área do corpo que não deseja receber massagem?",
          type: "textarea",
          enabled: true,
          system: true,
          placeholder: "Ex.: abdómen, pés, zona lombar...",
        },
      ],
    },
    {
      id: "aroma",
      title: "Aromaterapia e óleos essenciais",
      enabled: true,
      fields: [
        {
          key: "likes_essential_oils",
          label: "Gosta de óleos essenciais?",
          type: "switch",
          enabled: true,
          system: true,
          hint: "Usamos óleos 100% naturais para potenciar o relaxamento.",
        },
        {
          key: "aroma_consent",
          label: "Autoriza a utilização de óleos essenciais ou produtos aromáticos durante a sessão?",
          type: "yes_no",
          enabled: true,
          system: true,
          hint: "Pode alterar esta escolha a qualquer momento.",
        },
        {
          key: "aroma_preference",
          label: "Aroma preferido",
          type: "chips",
          enabled: true,
          system: true,
          options: AROMA_OPTIONS,
          showIf: "aroma_consent",
        },
        {
          key: "oil_allergies",
          label: "Tem alguma alergia ou sensibilidade a óleos, aromas ou cosméticos?",
          type: "textarea",
          enabled: true,
          system: true,
          placeholder: "Ex.: alergia a citrinos, pele sensível a óleos com álcool...",
        },
      ],
    },
    {
      id: "goals",
      title: "Objetivos e observações",
      enabled: true,
      fields: [
        { key: "observations", label: "Algum detalhe importante a considerar?", type: "textarea", enabled: true, system: true },
      ],
    },
    {
      id: "consent",
      title: "Consentimento informado",
      enabled: true,
      fields: [
        { key: "consent_accepted", label: "Li e aceito o consentimento acima.", type: "consent", enabled: true, required: true, system: true },
        { key: "signature_data", label: "Assinatura digital do cliente", type: "signature", enabled: true, required: true, system: true },
      ],
    },
    {
      id: "account",
      title: "Criar acesso à Área do Cliente",
      enabled: true,
      fields: [
        {
          key: "password",
          label: "Palavra-passe",
          type: "password",
          enabled: true,
          required: true,
          system: true,
          hint: "Defina uma palavra-passe para validar o seu registo. Com o seu email e esta palavra-passe poderá consultar a sua ficha, ver as suas reservas e pedir alterações a qualquer momento.",
        },
      ],
    },
  ],
});

const SYSTEM_KEYS = new Set(
  defaultFormConfig().sections.flatMap((s) => s.fields.map((f) => f.key)),
);

export const isSystemKey = (key: string) => SYSTEM_KEYS.has(key);

/** Merge a stored config with the defaults so new system fields are never lost */
export const mergeWithDefaults = (stored: any): ClientRecordFormConfig => {
  const base = defaultFormConfig();
  if (!stored || typeof stored !== "object" || !Array.isArray(stored.sections)) return base;

  const storedSections: FormSection[] = stored.sections;
  const result: FormSection[] = storedSections.map((s) => {
    const baseSection = base.sections.find((b) => b.id === s.id);
    const fields: FormField[] = (s.fields || []).map((f: FormField) => {
      const baseField = baseSection?.fields.find((b) => b.key === f.key);
      return { ...(baseField || {}), ...f, system: baseField ? true : !!isSystemKey(f.key) };
    });
    // add system fields introduced after the config was saved
    baseSection?.fields.forEach((bf) => {
      if (!fields.some((f) => f.key === bf.key)) fields.push({ ...bf });
    });
    return { ...(baseSection || {}), ...s, fields };
  });

  base.sections.forEach((bs) => {
    if (!result.some((s) => s.id === bs.id)) result.push(bs);
  });

  return {
    intro: typeof stored.intro === "string" ? stored.intro : base.intro,
    consent_text: typeof stored.consent_text === "string" ? stored.consent_text : base.consent_text,
    sections: result,
  };
};

export const loadFormConfig = async (): Promise<ClientRecordFormConfig> => {
  const { data } = await supabase
    .from("app_settings")
    .select("value")
    .eq("key", "client_record_form")
    .maybeSingle();
  return mergeWithDefaults(data?.value);
};

export const saveFormConfig = async (config: ClientRecordFormConfig) => {
  return supabase
    .from("app_settings")
    .upsert({ key: "client_record_form", value: config as any }, { onConflict: "key" });
};
